import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'

// Helper to create a consistent item structure
function createBasketItem({
    id = null,
    name = '',
    deliveryDay = 'Monday',
    price = 0,
    bulkPrices = [],
    quantity = 1,
    image = '',
    vat = false,
    category = 'General',
    bulkPricingToolTip = '',
} = {}) {
    return { id, name, deliveryDay, price, quantity, image, bulkPrices, vat, category, bulkPricingToolTip }
}

export const useBasketStore = defineStore('basket', () => {

    const items = ref([]);

    const itemCount = computed(() => items.value.length);

    const itemQuantityCount = computed(() => items.value.reduce((total, item) => total + item.quantity, 0));

    const full = computed(() => itemCount.value !== 0);

    const deliveryDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    const daysWithItems = computed(() => {
        const selectedDays = items.value.map(item => item.deliveryDay);

        return deliveryDays.filter(day => selectedDays.includes(day));
    });

    const categoriesWithItems = computed(() => {
        const selectedCategories = items.value.map(item => item.category);

        return [...new Set(selectedCategories)];
    });

    const itemsForDay = computed(() => {
        return (day) => items.value.filter(item => item.deliveryDay === day);
    });

    const itemsForCategory = computed(() => {
        return (category) => {
            const categoryItems = items.value.filter(item => item.category === category);
            return categoryItems.sort((a, b) => {
                const aIndex = deliveryDays.indexOf(a.deliveryDay);
                const bIndex = deliveryDays.indexOf(b.deliveryDay);
                return aIndex - bIndex;
            });
        };
    });

    const squashItemsIntoProductVariantLine = computed(() => {
        const squashed = {};
        categoriesWithItems.value.forEach(category => {
            const categoryItems = itemsForCategory.value(category);
            const uniqueProductNames = [...new Set(categoryItems.map(item => item.name))];
            squashed[category] = uniqueProductNames;
        });
        return squashed;
    });

    const categoryTotal = computed(() => {
        return (category) => {
            const categoryItems = itemsForCategory.value(category);
            return categoryItems.reduce((total, item) => total + lineItemSubtotal(item), 0);
        };
    });

    const totalExcTax = computed(() => {
        return items.value.reduce((total, item) => total + lineItemSubtotal(item), 0);
    });

    const totalTax = computed(() => {
        return items.value.reduce((total, item) => total + (item.vat ? lineItemSubtotal(item) * 0.2 : 0), 0);
    });

    const totalIncTax = computed(() => totalExcTax.value + totalTax.value);

    const basketSummary = computed(() => {
        let summary = '';

        deliveryDays.forEach(day => {
            const dayItems = itemsForDay.value(day);
            if (dayItems.length === 0 || dayItems.every(item => item.quantity === 0)) {
                return;
            }
            summary += `${day}:\n`;
            dayItems.forEach(item => {
                if (item.quantity === 0) {
                    return;
                }
                summary += `${item.name} x ${item.quantity} £${(lineItemSubtotal(item) + (item.vat ? lineItemSubtotal(item) * 0.2 : 0)).toFixed(2)}\n`;
            });
            summary += `Day Total: £${dayItems.reduce((total, item) => total + lineItemSubtotal(item) + (item.vat ? lineItemSubtotal(item) * 0.2 : 0), 0).toFixed(2)}\n\n`;
        });
        summary += `Basket Sub Total: £${totalExcTax.value.toFixed(2)}\n`;
        summary += `Basket Tax: £${totalTax.value.toFixed(2)}\n`;
        summary += `Basket Total: £${totalIncTax.value.toFixed(2)}\n`;

        return summary.trim();
    });

    const basketJson = computed(() => {
        return JSON.stringify(items.value);
    });

    function emitLineQuantityUpdated(item) {
        const event = new CustomEvent('basket-line-quantity-updated', {
            detail: {
                id: item.id,
                deliveryDay: item.deliveryDay,
                quantity: item.quantity,
                item
            }
        });
        window.dispatchEvent(event);
    }

    function addItem(newItem) {
        const existingItem = items.value.find(item => item.id === newItem.id && item.deliveryDay === newItem.deliveryDay);

        if (!existingItem) {
            items.value.push(createBasketItem(newItem));
            return;
        }

        existingItem.quantity += newItem.quantity;
    }

    function setItemQuantity(newItem) {
        const existing = items.value.find(item => item.id === newItem.id && item.deliveryDay === newItem.deliveryDay);

        if (!existing) {
            items.value.push(createBasketItem(newItem));
            emitLineQuantityUpdated(newItem);
            return;
        }

        existing.quantity = newItem.quantity;
        emitLineQuantityUpdated(existing);
    }

    function removeItem(id, deliveryDay) {
        const index = items.value.findIndex(item => item.id === id && item.deliveryDay === deliveryDay);

        if (index !== -1) {
            items.value.splice(index, 1);
        }
    }

    function removeAllItemsById(id) {
        items.value = items.value.filter(item => item.id !== id);
    }

    function clearItems() {
        items.value = [];
    }

    function lineItemSubtotal(item) {
        if (item.quantity === 0) {
            return 0;
        }

        if (item.quantity === 1) {
            return parseFloat(item.price);
        }

        if (!item.bulkPrices || item.bulkPrices.length === 0) {
            return parseFloat(item.price) * parseInt(item.quantity);
        }

        const bulkPrice = item.bulkPrices.find(bp => item.quantity == parseInt(bp.quantity));

        if (!bulkPrice) {
            const sortedBulkPrices = item.bulkPrices.slice().sort((a, b) => parseInt(b.quantity) - parseInt(a.quantity));
            const additionalItemPrice = parseFloat(sortedBulkPrices[0].price) - parseFloat(sortedBulkPrices[1].price);
            const additionalItemQuantity = parseInt(item.quantity) - parseInt(sortedBulkPrices[0].quantity);
            return (additionalItemPrice * additionalItemQuantity) + parseFloat(sortedBulkPrices[0].price);
        }

        return parseFloat(bulkPrice.price);
    }

    return { items, itemCount, itemQuantityCount, full, deliveryDays, daysWithItems, categoriesWithItems, itemsForDay, itemsForCategory, squashItemsIntoProductVariantLine, categoryTotal, totalExcTax, totalTax, totalIncTax, addItem, setItemQuantity, removeItem, removeAllItemsById, clearItems, lineItemSubtotal, basketSummary, basketJson }
}, {
    persist: true
});